<script lang="ts">
	import Catalog from './components/Catalog.svelte'
	import Login from './components/Login.svelte'
	import SessionPanel from './components/SessionPanel.svelte'
	import { auth, type AuthUser } from './lib/auth'
	import {
		fetchCatalog,
		findGame,
		gamesOfType,
		searchGames,
		UnauthorizedError,
		type Catalog as CatalogData,
		type GameType,
	} from './lib/catalog'
	import {
		DEFAULT_BALANCE,
		DEFAULT_CURRENCY,
		DEFAULT_LANGUAGE,
		LAUNCH_TARGET,
	} from './lib/config'
	import { launchUrl, type Session } from './lib/launch'
	import { storedPlayerId } from './lib/player'

	const session: Session = $state({
		player: storedPlayerId(),
		balance: DEFAULT_BALANCE,
		currency: DEFAULT_CURRENCY,
		language: DEFAULT_LANGUAGE,
	})

	// undefined = session check in flight, null = signed out
	let user = $state<AuthUser | null | undefined>(undefined)
	let catalog = $state<CatalogData | null>(null)
	let bootError = $state('')
	let query = $state('')
	let selected = $state('')
	// Releases are what the launcher is for; prototypes are the other drawer.
	let tab = $state<GameType>('game')

	$effect(() => {
		auth
			.me()
			.then((u) => {
				user = u
				loadCatalog()
			})
			.catch(() => (user = null))
	})

	function loadCatalog(): void {
		bootError = ''
		fetchCatalog()
			.then((data) => {
				catalog = data
				// Open on a release, which is the tab the launcher starts on.
				selected = (gamesOfType(data.games, 'game')[0] ?? data.games[0])?.slug ?? ''
				// The configured defaults are preferences; the server's catalog is
				// the authority on what exists.
				if (!data.currencies.includes(session.currency)) {
					session.currency = data.currencies[0] ?? session.currency
				}
				if (!data.languages.some((l) => l.code === session.language)) {
					session.language =
						data.languages.find((l) => l.code.startsWith('en'))?.code ??
						data.languages[0]?.code ??
						session.language
				}
			})
			.catch((err) => {
				if (err instanceof UnauthorizedError) {
					user = null
					catalog = null
				} else {
					bootError = 'The catalog service is unreachable. Retry in a moment.'
				}
			})
	}

	function signIn(u: AuthUser): void {
		user = u
		loadCatalog()
	}

	async function signOut(): Promise<void> {
		try {
			await auth.logout()
		} finally {
			user = null
			catalog = null
		}
	}

	const section = $derived(catalog ? gamesOfType(catalog.games, tab) : [])
	const matches = $derived(searchGames(section, query))
	const counts = $derived({
		game: catalog ? gamesOfType(catalog.games, 'game').length : 0,
		prototype: catalog ? gamesOfType(catalog.games, 'prototype').length : 0,
	})

	// Selection is held by slug and resolved against the whole catalogue, not the
	// filtered list: typing a search that hides the picked game is not the same
	// as picking a different one.
	const game = $derived(catalog && catalog.games.length ? findGame(catalog.games, selected) : null)
	const url = $derived(game ? launchUrl(game, session) : '')

	function launch(): void {
		window.open(url, LAUNCH_TARGET, 'noopener')
	}
</script>

{#if user === undefined}
	<div class="boot">checking session…</div>
{:else if user === null}
	<Login onlogin={signIn} />
{:else if bootError}
	<div class="boot error">{bootError}</div>
{:else if !catalog}
	<div class="boot">loading catalog…</div>
{:else if !game}
	<div class="boot">the catalog is empty — add a game in the back office</div>
{:else}
	<div class="shell">
		<SessionPanel
			{session}
			{game}
			{url}
			{user}
			currencies={catalog.currencies}
			languages={catalog.languages}
			onlaunch={launch}
			onlogout={signOut}
		/>
		<Catalog
			games={matches}
			total={section.length}
			{counts}
			bind:tab
			selected={game.slug}
			bind:query
			onpick={(slug) => (selected = slug)}
		/>
	</div>
{/if}

<style>
	.shell {
		display: flex;
		height: 100%;
		overflow: hidden;
	}

	.boot {
		height: 100%;
		display: flex;
		align-items: center;
		justify-content: center;
		font-size: 12px;
		color: var(--dim, #6e7681);
	}

	.boot.error {
		color: var(--danger-text, #ff9b9e);
	}
</style>
